import React from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';
import { AppSelect } from '../../../components/AppSelect';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import {
  getColorSchemePreference,
  setColorSchemePreference,
  subscribeColorSchemePreference,
  type ColorSchemePreference,
} from '../../../lib/colorSchemePreference';
import { invalidateMeCache } from '../../../lib/meClient';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import {
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
} from '../utils/settingsSectionStyles';

export function PersonalSettingsSection() {
  const [username, setUsername] = React.useState('');
  const [savedUsername, setSavedUsername] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [colorScheme, setColorScheme] = React.useState<ColorSchemePreference>(
    getColorSchemePreference,
  );
  const [schemeSaved, setSchemeSaved] = React.useState(false);
  const schemeSavedTimer = React.useRef<number | null>(null);

  React.useEffect(
    () => subscribeColorSchemePreference(setColorScheme),
    [],
  );

  React.useEffect(
    () => () => {
      if (schemeSavedTimer.current !== null) window.clearTimeout(schemeSavedTimer.current);
    },
    [],
  );

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSaved(false);
    setError(null);
    fetch('/api/me/preferences')
      .then(async (r) => {
        if (!r.ok) {
          throw new Error(
            (await r.json().catch(() => null))?.error ||
              'Persönliche Einstellungen konnten nicht geladen werden.',
          );
        }
        return r.json() as Promise<{ username?: string; email?: string }>;
      })
      .then((data) => {
        if (cancelled) return;
        setUsername(String(data.username || ''));
        setSavedUsername(String(data.username || ''));
        setEmail(String(data.email || ''));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const res = await fetch('/api/me/preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Speichern fehlgeschlagen.');
      setUsername(String(data?.username || username));
      setSavedUsername(String(data?.username || username));
      invalidateMeCache();
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const changeColorScheme = (value: string) => {
    const next = value as ColorSchemePreference;
    setColorScheme(next);
    setError(null);
    setSchemeSaved(false);
    void setColorSchemePreference(next)
      .then(() => {
        setSchemeSaved(true);
        // Switching again before the notice fades must not clear the newer confirmation early.
        if (schemeSavedTimer.current !== null) window.clearTimeout(schemeSavedTimer.current);
        schemeSavedTimer.current = window.setTimeout(() => setSchemeSaved(false), 2200);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : String(err));
      });
  };

  return (
    <div className="-mx-3 -mt-3 md:mx-0 md:mt-0 space-y-3 md:space-y-5">
      <SettingsCard title="Persönliche Einstellungen">
        <div className="space-y-4 max-w-xl">
          {error ? (
            <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
          ) : null}
          <div>
            <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>E-Mail</label>
            <input
              type="email"
              value={email}
              disabled
              className={`${SETTINGS_INPUT_CLASS} bg-app-surface-muted text-app-text-muted`}
            />
          </div>
          <div>
            <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>Nutzername</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading || saving}
              className={SETTINGS_INPUT_CLASS}
              placeholder="Dein Anzeigename"
            />
          </div>
          <div>
            <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>Darstellung</label>
            <AppSelect
              value={colorScheme}
              onChange={changeColorScheme}
              options={[
                { value: 'light', label: 'Hell', icon: <Sun className="h-4 w-4" aria-hidden /> },
                { value: 'dark', label: 'Dunkel', icon: <Moon className="h-4 w-4" aria-hidden /> },
                {
                  value: 'system',
                  label: 'Systemeinstellung',
                  icon: <Monitor className="h-4 w-4" aria-hidden />,
                },
              ]}
              triggerClassName="!min-h-0 h-9 !py-0"
              aria-label="Darstellung"
            />
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
              <p className="text-xs text-app-text-subtle">
                „Systemeinstellung“ folgt automatisch der Darstellung deines Geräts.
              </p>
              <SettingsCardSavedStatus saved={schemeSaved} className="text-xs" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={loading || saving || username.trim() === savedUsername.trim()}
              onClick={() => void save()}
              className={SETTINGS_BTN_PRIMARY}
            >
              {saving ? 'Speichert…' : 'Speichern'}
            </button>
            {loading ? <InlineLoadingState label="Lade…" className="justify-start" /> : null}
            <SettingsCardSavedStatus saved={saved} />
          </div>
        </div>
      </SettingsCard>
    </div>
  );
}
